import { type FormEvent, useState } from "react";
import { MessageSquare } from "lucide-react";
import RoundedCard from "@cle/components/RoundedCard";
import CleButton from "@cle/components/CleButton";
import {
  useAddSubjectComment,
  useSubjectReview,
} from "@/hooks/useDocumentReview";
import { formatDanishDateTime } from "@/util/date";

type SecretariatMessageCardProps = {
  subjectId: string;
  isReported?: boolean;
  onCommentSent?: () => void;
};

const DocumentCommentsSkeleton = () => (
  <div className="placeholder-glow d-flex flex-column gap-3">
    {[0, 1].map((item) => (
      <div
        key={item}
        className="cle-course-certificate-document__message"
        aria-hidden
      >
        <div className="d-flex justify-content-between gap-3 mb-2">
          <span className="placeholder col-4 rounded" />
          <span className="placeholder col-3 rounded" />
        </div>
        <span className="placeholder col-11 rounded d-block mb-2" />
        <span className="placeholder col-8 rounded d-block" />
      </div>
    ))}
  </div>
);

const SecretariatMessageCard = ({
  subjectId,
  isReported = false,
  onCommentSent,
}: SecretariatMessageCardProps) => {
  const [commentContent, setCommentContent] = useState("");
  const {
    data: subjectReview,
    isError,
    isLoading,
  } = useSubjectReview(subjectId);
  const comments = subjectReview?.comments ?? [];
  const addSubjectCommentMutation = useAddSubjectComment();
  const trimmedCommentContent = commentContent.trim();
  const isSubmitDisabled =
    subjectId.length === 0 ||
    trimmedCommentContent.length === 0 ||
    addSubjectCommentMutation.isPending;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSubmitDisabled) {
      return;
    }

    addSubjectCommentMutation.mutate(
      {
        subjectId,
        content: trimmedCommentContent,
      },
      {
        onSuccess: () => {
          setCommentContent("");
          onCommentSent?.();
        },
      },
    );
  };

  // Reported (indberettet) evidence takes no new comments; when there is no
  // existing conversation to show, hide the card entirely.
  if (isReported && !isLoading && !isError && comments.length === 0) {
    return null;
  }

  return (
    <RoundedCard
      className="cle-course-certificate-document__side-card"
      header={
        <span className="d-inline-flex align-items-center gap-2">
          <MessageSquare size={20} aria-hidden strokeWidth={1.9} />
          Besked til sekretariatet
        </span>
      }
    >
      {isLoading ? <DocumentCommentsSkeleton /> : null}
      {isError ? (
        <div className="alert alert-warning mb-0" role="alert">
          Beskeder kunne ikke indlæses lige nu.
        </div>
      ) : null}
      {!isLoading && !isError && comments.length === 0 ? (
        <div className="cle-course-certificate-document__message text-muted">
          Der er endnu ingen beskeder på dokumentet.
        </div>
      ) : null}
      {!isLoading && !isError && comments.length > 0 ? (
        <div className="d-flex flex-column gap-3">
          {comments.map((comment) => (
            <div
              key={comment.id}
              className="cle-course-certificate-document__message"
            >
              <div className="d-flex justify-content-between gap-3 mb-2">
                <strong>{comment.actorDisplayName ?? "Ukendt"}</strong>
                <span className="text-muted small">
                  {formatDanishDateTime(comment.createdAt)}
                </span>
              </div>
              <p className="mb-0">{comment.content}</p>
            </div>
          ))}
        </div>
      ) : null}
      {isReported ? (
        <p className="text-muted mb-0">
          Beviset er indberettet, og der kan derfor ikke tilføjes nye
          kommentarer.
        </p>
      ) : (
        <form onSubmit={handleSubmit}>
          <label
            className="visually-hidden"
            htmlFor="cle-course-certificate-comment"
          >
            Tilføj kommentar
          </label>
          <textarea
            id="cle-course-certificate-comment"
            className="form-control cle-course-certificate-document__textarea"
            disabled={addSubjectCommentMutation.isPending}
            onChange={(event) => setCommentContent(event.target.value)}
            placeholder="Tilføj en kommentar..."
            rows={4}
            value={commentContent}
          />
          {addSubjectCommentMutation.isError ? (
            <div className="alert alert-warning mt-3 mb-0" role="alert">
              Beskeden kunne ikke sendes lige nu. Prøv igen.
            </div>
          ) : null}
          <CleButton
            id="cle-course-certificate-add-comment"
            className="w-100 mt-3"
            disabled={isSubmitDisabled}
            type="submit"
          >
            {addSubjectCommentMutation.isPending
              ? "Sender..."
              : "Tilføj Kommentar"}
          </CleButton>
        </form>
      )}
    </RoundedCard>
  );
};

export default SecretariatMessageCard;
